import { Component, type ReactNode } from 'react';

interface FlowThemeErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the theme once it has failed (e.g. the static swatch, or nothing). */
  fallback?: ReactNode;
  /** Changing this (e.g. the theme id) gives a failed theme another try. */
  resetKey?: string;
  onError?: (error: unknown) => void;
}

interface FlowThemeErrorBoundaryState {
  failed: boolean;
}

/**
 * Keeps a broken theme (a failed chunk or scene load, or a render error in the
 * backdrop or its bursts) from unmounting the flow around it.
 */
export class FlowThemeErrorBoundary extends Component<FlowThemeErrorBoundaryProps, FlowThemeErrorBoundaryState> {
  state: FlowThemeErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): FlowThemeErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.warn('Flow theme failed; falling back to the static backdrop', error);
    this.props.onError?.(error);
  }

  componentDidUpdate(previous: FlowThemeErrorBoundaryProps) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    return this.state.failed ? this.props.fallback ?? null : this.props.children;
  }
}
